import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, ExternalLink, Star, Check, X as XIcon, Play, Image, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { publicApi, stackApi } from '../utils/api';
import { buildToolGoUrl } from '../utils/affiliateLinks';
import ToolTransparencyWidget from '../components/ToolTransparencyWidget';
import ToolPrimingLayer from '../components/ToolPrimingLayer';
import StackConsultingPrime from '../components/StackConsultingPrime';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { mergeGuestStackTools, parseGuestStack, saveGuestStack } from '../utils/runningStackStore';
import { loadBusinessContext } from '../utils/businessContext';

const parseMonthlyCost = (priceText) => {
  const raw = String(priceText || '').toLowerCase().replace(/,/g, '');
  if (!raw) return null;
  if (raw.includes('free')) return 0;
  if (raw.includes('custom')) return null;
  const match = raw.match(/\$\s*(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const amount = Number(match[1]);
  if (Number.isNaN(amount)) return null;
  if (/\/(year|yr)|per year|annual|annually/.test(raw)) {
    return amount / 12;
  }
  return amount;
};

const getDefaultMonthlyCost = (tool) => {
  const roiConfig = tool?.showcase_tabs?.roi || {};
  const explicit = Number(roiConfig.monthly_tool_cost);
  if (Number.isFinite(explicit) && explicit >= 0) {
    return explicit;
  }

  const costs = (tool?.pricing_tiers || [])
    .map((tier) => parseMonthlyCost(tier?.price))
    .filter((value) => value !== null && value >= 0);

  if (!costs.length) {
    return 0;
  }

  const paid = costs.filter((value) => value > 0);
  return paid.length ? Math.min(...paid) : 0;
};

const downloadSourceJson = (tool) => {
  const payload = tool?.last_source_json;
  if (!payload || typeof payload !== 'object' || Object.keys(payload).length === 0) {
    return;
  }

  const fileName = tool?.last_source_json_file_name || `${tool?.slug || 'tool'}-source.json`;
  const content = JSON.stringify(payload, null, 2);
  const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const getVideoEmbedSrc = (tool) => {
  const rawEmbed = String(tool?.video_embed || '').trim();
  if (rawEmbed) {
    const srcMatch = rawEmbed.match(/src=["']([^"']+)["']/i);
    if (srcMatch?.[1]) {
      return srcMatch[1];
    }
    if (/^https?:\/\//i.test(rawEmbed)) {
      return rawEmbed;
    }
  }

  const rawVideo = String(tool?.video_url || '').trim();
  if (!rawVideo) return '';
  if (rawVideo.includes('watch?v=')) {
    return rawVideo.replace('watch?v=', 'embed/');
  }
  if (rawVideo.includes('youtu.be/')) {
    const id = rawVideo.split('youtu.be/')[1]?.split(/[?&]/)[0];
    if (id) return `https://www.youtube.com/embed/${id}`;
  }
  return rawVideo;
};

const TOOL_LOGO_PLACEHOLDER = '/images/tool-placeholder.svg';

const isPlaceholderLogoUrl = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return true;
  if (raw.includes('tool-placeholder.svg') || raw.includes('ui-avatars.com')) return true;
  try {
    const parsed = new URL(raw, window.location.origin);
    const host = (parsed.hostname || '').toLowerCase();
    const path = (parsed.pathname || '').toLowerCase();
    if (host.includes('google.com') && path.includes('/s2/favicons')) return true;
    return host.includes('logo.clearbit.com') || host.includes('gstatic.com');
  } catch {
    return false;
  }
};

const buildFirstPartyFaviconUrl = (websiteUrl) => {
  try {
    const normalizedUrl = String(websiteUrl || '').trim();
    if (!normalizedUrl) return '';
    const parsed = new URL(normalizedUrl.startsWith('http') ? normalizedUrl : `https://${normalizedUrl}`);
    const host = parsed.hostname.replace(/^www\./, '');
    return host ? `https://${host}/favicon.ico` : '';
  } catch {
    return '';
  }
};

const getDisplayLogoUrl = (tool) => {
  const logoUrl = String(tool?.logo_url || '').trim();
  if (logoUrl && !isPlaceholderLogoUrl(logoUrl)) {
    return logoUrl;
  }

  const faviconUrl = String(tool?.favicon_url || '').trim();
  if (faviconUrl) {
    return faviconUrl;
  }

  return buildFirstPartyFaviconUrl(tool?.website_url) || TOOL_LOGO_PLACEHOLDER;
};

const dedupeUrlList = (values = []) => {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const url = String(value || '').trim();
    if (!url) return;
    if (seen.has(url)) return;
    seen.add(url);
    out.push(url);
  });
  return out;
};

const ToolDetailPage = () => {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGalleryImage, setActiveGalleryImage] = useState(0);
  const [activeTab, setActiveTab] = useState('summary');
  const [stackMessage, setStackMessage] = useState('');
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [logoImageIndex, setLogoImageIndex] = useState(0);
  const { isAuthenticated, registerPublic, loginPublic } = useAuth();
  const activeBusinessContext = useMemo(() => loadBusinessContext(isAuthenticated), [isAuthenticated]);

  const loadTool = useCallback(async () => {
    try {
      const response = await publicApi.getToolBySlug(slug);
      setTool(response.data);
    } catch (error) { console.error('Failed to load tool:', error); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { loadTool(); }, [loadTool]);

  const logoCandidates = useMemo(() => {
    if (!tool) return [];
    return dedupeUrlList([
      getDisplayLogoUrl(tool),
      tool?.favicon_url,
      buildFirstPartyFaviconUrl(tool?.website_url),
      TOOL_LOGO_PLACEHOLDER,
    ]);
  }, [tool]);

  const heroImageCandidates = useMemo(() => {
    if (!tool) return [];
    return dedupeUrlList([
      tool?.cover_image,
      tool?.background_image,
      ...(tool?.gallery_images || []),
      ...(tool?.screenshot_urls || []),
    ]);
  }, [tool]);

  useEffect(() => {
    setHeroImageIndex(0);
    setLogoImageIndex(0);
  }, [tool?.id]);

  const handleVisitClick = async () => {
    if (tool) {
      try { await publicApi.trackToolClick(tool.id); } catch (error) {}
    }
  };

  const addToLocalGuestStack = (stackTool) => {
    try {
      const current = parseGuestStack();
      const next = mergeGuestStackTools(current, [stackTool]);
      const added = next.length > current.length;
      if (added) {
        saveGuestStack(next);
      }
      return added;
    } catch (error) {
      return false;
    }
  };

  const handleAddToStack = async () => {
    if (!tool) return;

    if (!isAuthenticated) {
      const stored = addToLocalGuestStack({
        tool_id: tool.id,
        slug: tool.slug,
        name: tool.name,
        category_name: '',
        monthly_cost: tool.pricing_tiers?.[0]?.price || '0',
        notes: '',
        added_date: new Date().toISOString(),
      });
      setStackMessage(stored ? 'Added to local My Stack. Create an account to persist and get smart alerts.' : 'Already in your local My Stack.');
      setShowAccountPrompt(true);
      return;
    }

    try {
      await stackApi.addStackItem({ tool_id: tool.id, notes: '' });
      setStackMessage('Added to My Stack dashboard.');
    } catch (error) {
      setStackMessage('Could not add to stack right now.');
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthBusy(true);

    const result = authMode === 'register'
      ? await registerPublic({ email: authEmail, password: authPassword, fullName: authName || 'Wowrolla User' })
      : await loginPublic(authEmail, authPassword);

    if (!result.success) {
      setAuthError(result.error || 'Authentication failed');
      setAuthBusy(false);
      return;
    }

    if (authMode === 'register' && result.requiresConfirmation) {
      setStackMessage(result.message || 'Account created. Confirm your email, then log in to sync this local stack to your dashboard.');
      setShowAccountPrompt(false);
      setAuthBusy(false);
      return;
    }

    try {
      await stackApi.addStackItem({ tool_id: tool.id, notes: '' });
      setStackMessage('Account created and tool persisted to your stack dashboard.');
      setShowAccountPrompt(false);
    } catch (error) {
      setStackMessage('Account created. Visit your stack dashboard to sync local saves.');
    } finally {
      setAuthBusy(false);
    }
  };

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    setNewsletterMessage('');
    try {
      const response = await publicApi.newsletterSubscribe({
        email: newsletterEmail,
        source_page: `/tools/${slug}`,
        role: null,
      });
      setNewsletterMessage(response.data?.message || 'Confirmation email sent.');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterMessage(error?.response?.data?.detail || 'Unable to subscribe right now.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Tool not found</h2>
          <Link to="/" style={{ color: 'var(--accent-primary)' }}>Return to homepage</Link>
        </div>
      </div>
    );
  }

  const visitUrl = buildToolGoUrl(tool);
  const displayLogoUrl = logoCandidates[logoImageIndex] || '';
  const displayHeroImage = heroImageCandidates[heroImageIndex] || '';
  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'overview', label: 'Overview' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'roi', label: 'ROI' },
    { id: 'changelog', label: 'Changelog' },
    { id: 'pricing', label: 'Pricing' },
  ];

  const lastVerified = (() => {
    const raw = tool.updated_at || tool.created_at;
    if (!raw) {
      return 'Recently';
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime())
      ? 'Recently'
      : date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="min-h-screen pt-20 page-bg-shell page-bg-code" data-testid="tool-detail-page">
      <SEOHead
        pageType="tool"
        title={tool.meta_title || `${tool.name} Review and Pricing`}
        description={tool.meta_description || tool.tagline || tool.description}
        slug={`tools/${tool.slug}`}
        tool={tool}
        dateModified={tool.updated_at || tool.created_at}
        author="Wowrolla Editorial"
        category={tool.category_name || 'AI Tool'}
      />

      <ToolPrimingLayer tool={tool} />

      <section
        className="relative"
        style={{
          background: tool.background_image
            ? `linear-gradient(180deg, rgba(0,0,0,0.68), rgba(3,8,20,0.92)), url(${tool.background_image}) center/cover no-repeat`
            : undefined
        }}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="max-w-5xl mb-4">
            <p className="text-[11px] uppercase tracking-[0.24em] font-mono mb-3" style={{ color: 'var(--accent-primary)' }}>
              Tool intelligence
            </p>
            <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }} data-testid="tool-title">
              {tool.name}
            </h1>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Last verified: {lastVerified} · Updated when tools change pricing or features
            </p>
            {tool.tagline && <p className="text-lg max-w-3xl" style={{ color: 'var(--accent-primary)' }}>{tool.tagline}</p>}
          </div>

          <div className="terminal-grid p-2 overflow-x-auto sticky top-20 z-20 backdrop-blur-sm" data-testid="tool-tabs">
            <div className="flex gap-2 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'} text-sm font-semibold whitespace-nowrap px-4 py-2`}
                  data-testid={`tool-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {activeTab === 'summary' && (
        <ToolSummaryTab
          tool={tool}
          visitUrl={visitUrl}
          handleVisitClick={handleVisitClick}
          handleAddToStack={handleAddToStack}
          stackMessage={stackMessage}
          activeBusinessContext={activeBusinessContext}
          displayLogoUrl={displayLogoUrl}
          displayHeroImage={displayHeroImage}
          logoCandidates={logoCandidates}
          heroImageCandidates={heroImageCandidates}
          setLogoImageIndex={setLogoImageIndex}
          setHeroImageIndex={setHeroImageIndex}
          newsletterEmail={newsletterEmail}
          setNewsletterEmail={setNewsletterEmail}
          newsletterSubmitting={newsletterSubmitting}
          newsletterMessage={newsletterMessage}
          handleNewsletterSignup={handleNewsletterSignup}
        />
      )}

      {activeTab === 'overview' && (
        <ToolOverviewTab tool={tool} activeGalleryImage={activeGalleryImage} setActiveGalleryImage={setActiveGalleryImage} />
      )}
      {activeTab === 'reviews' && <ToolReviewsTab tool={tool} />}
      {activeTab === 'pricing' && <ToolPricingTab tool={tool} />}
      {activeTab === 'changelog' && <ToolChangelogTab tool={tool} />}
      {activeTab === 'roi' && <ToolRoiTab tool={tool} />}

      <section className="container mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tool-outcome-cards">
          <motion.div whileHover={{ y: -4 }} className="prime-glow-card p-4">
            <p className="text-xs uppercase font-mono text-text-muted mb-1">Outcome</p>
            <p className="text-white font-semibold inline-flex items-center gap-2"><Zap size={15} className="text-laser-green" />Faster implementation decisions</p>
          </motion.div>
          <motion.div whileHover={{ y: -4 }} className="prime-glow-card p-4">
            <p className="text-xs uppercase font-mono text-text-muted mb-1">Outcome</p>
            <p className="text-white font-semibold inline-flex items-center gap-2"><ShieldCheck size={15} className="text-cyber-yellow" />Lower risk of vendor mismatch</p>
          </motion.div>
          <motion.div whileHover={{ y: -4 }} className="prime-glow-card p-4">
            <p className="text-xs uppercase font-mono text-text-muted mb-1">Outcome</p>
            <p className="text-white font-semibold inline-flex items-center gap-2"><Sparkles size={15} className="text-cyber-yellow" />Clearer ROI expectation before rollout</p>
          </motion.div>
          <motion.div whileHover={{ y: -4 }} className="prime-glow-card p-0 overflow-hidden hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=900&q=80"
              alt="Strategic AI planning"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
        <StackConsultingPrime compact />
      </section>

      {showAccountPrompt && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-6" data-testid="stack-account-prompt">
          <div className="terminal-grid p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
              Persist Your Stack
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Your tool is saved locally. Create an account to sync it across devices and activate smart alerts.
            </p>

            <div className="flex gap-2 mb-4">
              <button onClick={() => setAuthMode('register')} className="px-3 py-2 border text-sm" style={{ borderColor: authMode === 'register' ? 'var(--accent-primary)' : 'var(--border-subtle)', color: 'var(--text-primary)' }}>Create account</button>
              <button onClick={() => setAuthMode('login')} className="px-3 py-2 border text-sm" style={{ borderColor: authMode === 'login' ? 'var(--accent-primary)' : 'var(--border-subtle)', color: 'var(--text-primary)' }}>Login</button>
            </div>

            <form onSubmit={handleAccountSubmit} className="space-y-3">
              {authMode === 'register' && (
                <input value={authName} onChange={(e) => setAuthName(e.target.value)} placeholder="Full name" className="w-full px-3 py-2 bg-transparent border border-white/20" style={{ color: 'var(--text-primary)' }} />
              )}
              <input type="email" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} placeholder="Email" className="w-full px-3 py-2 bg-transparent border border-white/20" style={{ color: 'var(--text-primary)' }} required />
              <input type="password" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} placeholder="Password" className="w-full px-3 py-2 bg-transparent border border-white/20" style={{ color: 'var(--text-primary)' }} required />

              {authError && <p className="text-sm" style={{ color: '#ff7777' }}>{authError}</p>}

              <button type="submit" disabled={authBusy} className="w-full px-4 py-2 font-semibold" style={{ background: '#FFE800', color: '#000' }}>
                {authBusy ? 'Please wait...' : authMode === 'register' ? 'Create account & sync stack' : 'Login & sync stack'}
              </button>
            </form>

            <Link
              to={`/login?redirect=${encodeURIComponent('/dashboard/stack')}`}
              className="w-full mt-3 px-4 py-2 border inline-flex items-center justify-center"
              style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}
            >
              Continue with Google or username login
            </Link>

            <div className="flex justify-end mt-4">
              <button onClick={() => setShowAccountPrompt(false)} className="text-sm" style={{ color: 'var(--text-muted)' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ToolSummaryTab = ({
  tool,
  visitUrl,
  handleVisitClick,
  handleAddToStack,
  stackMessage,
  activeBusinessContext,
  displayLogoUrl,
  displayHeroImage,
  logoCandidates,
  heroImageCandidates,
  setLogoImageIndex,
  setHeroImageIndex,
  newsletterEmail,
  setNewsletterEmail,
  newsletterSubmitting,
  newsletterMessage,
  handleNewsletterSignup,
}) => (
  <section className="container mx-auto px-6 py-8" data-testid="tool-summary-tab">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
      <div className="lg:col-span-2">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {(displayHeroImage || displayLogoUrl || tool.background_image) && (
            <div className="mb-5 grid grid-cols-1 sm:grid-cols-[132px_1fr] gap-4">
              <div className="h-[132px] p-4 rounded-2xl flex items-center justify-center" style={{ border: `1px solid var(--border-subtle)`, background: 'rgba(9,15,32,0.82)' }}>
                {displayLogoUrl ? (
                  <img
                    src={displayLogoUrl}
                    alt={`${tool.name} logo`}
                    className="w-full h-full object-contain"
                    onError={() => setLogoImageIndex((prev) => (prev + 1 < logoCandidates.length ? prev + 1 : prev))}
                  />
                ) : (
                  <span className="text-xs uppercase font-mono" style={{ color: 'var(--text-muted)' }}>No logo</span>
                )}
              </div>
              <div className="h-[132px] rounded-2xl overflow-hidden" style={{ border: `1px solid var(--border-subtle)` }}>
                <div className="relative w-full h-full">
                  <img
                    src={displayHeroImage || tool.background_image}
                    alt={`${tool.name} preview`}
                    className="w-full h-full object-cover"
                    onError={() => setHeroImageIndex((prev) => (prev + 1 < heroImageCandidates.length ? prev + 1 : prev))}
                  />
                  {heroImageCandidates.length > 1 && (
                    <>
                      <button
                        type="button"
                        aria-label="Previous image"
                        onClick={() => setHeroImageIndex((prev) => (prev - 1 + heroImageCandidates.length) % heroImageCandidates.length)}
                        className="btn-secondary absolute left-3 top-1/2 -translate-y-1/2 px-2 py-2 rounded-full"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        aria-label="Next image"
                        onClick={() => setHeroImageIndex((prev) => (prev + 1) % heroImageCandidates.length)}
                        className="btn-secondary absolute right-3 top-1/2 -translate-y-1/2 px-2 py-2 rounded-full"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {(activeBusinessContext.businessType || activeBusinessContext.selectedWorkflows?.length) && (
            <div className="mb-5 p-4 rounded-2xl" style={{ border: '1px solid rgba(0,212,255,0.35)', background: 'linear-gradient(135deg, rgba(0,212,255,0.12), rgba(0,212,255,0.04))' }}>
              <p className="text-[10px] uppercase tracking-widest font-mono" style={{ color: '#00D4FF' }}>AI Advisor Context</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>
                Business type: {activeBusinessContext.businessType || 'n/a'}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                Workflow priorities: {activeBusinessContext.selectedWorkflows?.length
                  ? activeBusinessContext.selectedWorkflows.join(' | ')
                  : 'not selected yet'}
              </p>
            </div>
          )}

          {tool.rating && (
            <div className="flex items-center gap-4 mb-5 flex-wrap">
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} style={{ color: i < Math.floor(tool.rating.overall) ? 'var(--accent-primary)' : 'var(--text-muted)', fill: i < Math.floor(tool.rating.overall) ? 'var(--accent-primary)' : 'none' }} />
                ))}
              </div>
              <span className="text-lg font-mono" style={{ color: 'var(--text-primary)' }}>{tool.rating.overall.toFixed(1)} / 5.0</span>
            </div>
          )}

          <div className="terminal-grid p-6 rounded-2xl">
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>{tool.description}</p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tool-scarcity-adoption-strip">
              <div className="p-3 rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-xs uppercase font-mono text-text-muted">Competitor adoption</p>
                <p className="text-lg text-white font-semibold">{tool.competitor_adoption_pct ? `${tool.competitor_adoption_pct}%` : 'n/a'}</p>
              </div>
              <div className="p-3 rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-xs uppercase font-mono text-text-muted">Time saving potential</p>
                <p className="text-lg text-white font-semibold">{tool.time_saving_rate ? `${tool.time_saving_rate}%` : 'n/a'}</p>
              </div>
              <div className="p-3 rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-xs uppercase font-mono text-text-muted">Implementation complexity</p>
                <p className="text-lg text-white font-semibold">{tool.implementation_complexity || 'n/a'}</p>
              </div>
            </div>

            <div className="mt-6 flex gap-3 flex-wrap">
              <a href={visitUrl} target="_blank" rel="sponsored nofollow noopener noreferrer" onClick={handleVisitClick}
                className="btn-primary inline-flex items-center gap-2" data-testid="tool-visit-button">
                Buy {tool.name}
                <ExternalLink size={18} />
              </a>
              <button type="button" onClick={handleAddToStack} className="btn-secondary" data-testid="tool-add-to-stack-button">Add to My Stack</button>
              <Link to={`/compare?tools=${tool.slug}`} className="btn-secondary" data-testid="tool-compare-link">Compare alternatives</Link>
              <Link to={`/alternatives/${tool.slug}`} className="btn-secondary" data-testid="tool-alternatives-link">Alternatives page</Link>
            </div>
            {stackMessage && <p className="mt-3 text-sm" style={{ color: 'var(--accent-primary)' }}>{stackMessage}</p>}
          </div>
        </motion.div>
      </div>

      <div>
        <div className="terminal-grid p-5 sticky top-40">
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Quick Info</h3>

          {tool.pricing_tiers?.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm mb-2 uppercase font-mono" style={{ color: 'var(--text-muted)' }}>Pricing</h4>
              <div className="space-y-2">
                {tool.pricing_tiers.map((tier, i) => (
                  <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>{tier.name}</div>
                    <div style={{ color: 'var(--accent-primary)' }}>{tier.price}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ToolTransparencyWidget affiliateLink={tool.affiliate_link} />

          <div className="mt-6 p-4 rounded-2xl" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }} data-testid="tool-newsletter-capture">
            <h4 className="text-sm mb-2 uppercase font-mono" style={{ color: 'var(--text-muted)' }}>Weekly AI Digest</h4>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>One expert note every Monday. Double opt-in enabled.</p>
            <form onSubmit={handleNewsletterSignup} className="space-y-2">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="input-pro"
                placeholder="you@company.com"
                required
              />
              <button type="submit" className="btn-primary w-full justify-center" disabled={newsletterSubmitting}>
                {newsletterSubmitting ? 'Submitting...' : 'Subscribe'}
              </button>
            </form>
            {newsletterMessage && <p className="text-xs mt-2" style={{ color: 'var(--accent-primary)' }}>{newsletterMessage}</p>}
          </div>

          {tool.best_for?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm mb-2 uppercase font-mono" style={{ color: 'var(--text-muted)' }}>Best For</h4>
              <ul className="space-y-2">
                {tool.best_for.map((item, i) => (
                  <li key={i} className="text-sm flex items-start gap-2 rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--accent-secondary)' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

const ToolOverviewTab = ({ tool, activeGalleryImage, setActiveGalleryImage }) => {
  const tabOverview = tool?.showcase_tabs?.overview || {};
  const galleryImages = tool.gallery_images?.length
    ? tool.gallery_images
    : (tabOverview.gallery_images || []).filter(Boolean);
  const hasSourceJson = tool?.last_source_json && Object.keys(tool.last_source_json).length > 0;
  const videoEmbedSrc = getVideoEmbedSrc(tool);

  return (
    <>
    {videoEmbedSrc && (
      <section className="container mx-auto px-6 py-8">
        <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
          <Play size={24} className="inline mr-2" style={{ color: 'var(--accent-primary)' }} />
          Video Overview
        </h3>
        <div className="terminal-grid overflow-hidden rounded-lg" style={{ aspectRatio: '16/9' }}>
          <iframe
            src={videoEmbedSrc}
            title={`${tool.name} video`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </section>
    )}

    {galleryImages.length > 0 && (
      <section className="container mx-auto px-6 py-8">
        <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
          <Image size={24} className="inline mr-2" style={{ color: 'var(--accent-primary)' }} />
          Gallery
        </h3>
        <div className="terminal-grid overflow-hidden rounded-lg mb-4 relative">
          <img
            src={galleryImages[Math.min(activeGalleryImage, Math.max(galleryImages.length - 1, 0))]}
            alt={`${tool.name} screenshot ${activeGalleryImage + 1}`}
            className="w-full h-[420px] object-contain object-center"
            style={{ background: 'var(--bg-elevated)' }}
          />
          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous gallery image"
                onClick={() => setActiveGalleryImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full border"
                style={{ background: 'rgba(0,0,0,0.45)', borderColor: 'var(--border-subtle)', color: 'white' }}
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                aria-label="Next gallery image"
                onClick={() => setActiveGalleryImage((prev) => (prev + 1) % galleryImages.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full border"
                style={{ background: 'rgba(0,0,0,0.45)', borderColor: 'var(--border-subtle)', color: 'white' }}
              >
                <ArrowRight size={18} />
              </button>
            </>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {galleryImages.map((img, i) => (
            <button key={i} onClick={() => setActiveGalleryImage(i)}
              className="w-20 h-14 rounded overflow-hidden flex-shrink-0 transition-all"
              style={{ border: `2px solid ${activeGalleryImage === i ? 'var(--accent-primary)' : 'var(--border-subtle)'}`, opacity: activeGalleryImage === i ? 1 : 0.6 }}>
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </section>
    )}

    <section className="container mx-auto px-6 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {tool.pros?.length > 0 && (
          <div className="terminal-grid p-8">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Pros</h3>
            <ul className="space-y-3">
              {tool.pros.map((pro, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={20} className="mt-1 flex-shrink-0" style={{ color: 'var(--accent-secondary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>{pro}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {tool.cons?.length > 0 && (
          <div className="terminal-grid p-8">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Cons</h3>
            <ul className="space-y-3">
              {tool.cons.map((con, i) => (
                <li key={i} className="flex items-start gap-3">
                  <XIcon size={20} className="text-red-500 mt-1 flex-shrink-0" />
                  <span style={{ color: 'var(--text-secondary)' }}>{con}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {tool.features?.length > 0 && (
        <div className="mt-8 terminal-grid p-8">
          <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tool.features.map((feature, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--accent-primary)' }}></div>
                <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasSourceJson && (
        <div className="mt-8 terminal-grid p-8" data-testid="tool-source-json-panel">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Last Source JSON Snapshot</h3>
            <button type="button" className="btn-secondary text-sm" onClick={() => downloadSourceJson(tool)}>
              Download JSON
            </button>
          </div>
          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            This is the most recent structured payload captured from the official website and saved on this tool record.
          </p>
          <div className="text-xs font-mono overflow-x-auto p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>
            {tool.last_source_json_file_name || `${tool.slug}-source.json`}
          </div>
        </div>
      )}
    </section>
  </>
  );
};

const ToolReviewsTab = ({ tool }) => {
  const reviewSignals = (tool?.showcase_tabs?.reviews?.snippets || []).filter(Boolean);
  const reviewSources = (tool?.showcase_tabs?.reviews?.sources || []).filter(Boolean);
  const sourceAverages = (tool?.showcase_tabs?.reviews?.source_averages || []).filter(Boolean);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companySizeFilter, setCompanySizeFilter] = useState('all');
  const [form, setForm] = useState({
    author_name: '',
    rating: 5,
    title: '',
    body: '',
    role_profile: '',
    company_size: '',
  });

  useEffect(() => {
    let mounted = true;
    const loadReviews = async () => {
      try {
        const [response, summaryResponse] = await Promise.all([
          publicApi.getToolReviews(tool.id),
          publicApi.getToolReviewsSummary(tool.id),
        ]);
        if (mounted) {
          setReviews(response.data || []);
          setSummary(summaryResponse.data || null);
        }
      } catch (error) {
        if (mounted) {
          setReviews([]);
          setSummary(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadReviews();
    return () => {
      mounted = false;
    };
  }, [tool.id]);

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmitMessage('');
    setSubmitting(true);
    try {
      await publicApi.createReview({
        tool_id: tool.id,
        author_name: form.author_name,
        rating: Number(form.rating),
        title: form.title,
        body: form.body,
        role_profile: form.role_profile,
        company_size: form.company_size,
        source_label: 'Community',
      });
      setSubmitMessage('Review submitted for moderation. It appears once approved.');
      setForm({ author_name: '', rating: 5, title: '', body: '', role_profile: '', company_size: '' });
    } catch (error) {
      setSubmitMessage(error?.response?.data?.detail || 'Could not submit review right now.');
    } finally {
      setSubmitting(false);
    }
  };

  const wordCount = (form.body || '').trim() ? (form.body || '').trim().split(/\s+/).length : 0;
  const roles = Array.from(new Set(reviews.map((review) => review.role_profile).filter(Boolean))).sort();
  const companySizes = Array.from(new Set(reviews.map((review) => review.company_size).filter(Boolean))).sort();
  const filteredReviews = reviews.filter((review) => {
    const roleOk = roleFilter === 'all' || review.role_profile === roleFilter;
    const sizeOk = companySizeFilter === 'all' || review.company_size === companySizeFilter;
    return roleOk && sizeOk;
  });
  const communityAverage = Number(summary?.average_rating || 0);
  const sourceAverage = sourceAverages.length
    ? sourceAverages.reduce((sum, item) => sum + Number(item.average_rating || 0), 0) / sourceAverages.length
    : 0;
  const blendedAverage = (() => {
    if (communityAverage > 0 && sourceAverage > 0) {
      return ((communityAverage + sourceAverage) / 2).toFixed(2);
    }
    if (communityAverage > 0) return communityAverage.toFixed(2);
    if (sourceAverage > 0) return sourceAverage.toFixed(2);
    return 'n/a';
  })();

  return (
    <section className="container mx-auto px-6 py-10" data-testid="tool-reviews-tab">
      <div className="terminal-grid p-8 mb-6">
        <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Reviews</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, rgba(255,232,0,0.20), rgba(255,232,0,0.05))', border: '1px solid rgba(255,232,0,0.5)' }}>
            <p className="text-xs uppercase font-mono mb-1" style={{ color: '#FFE800' }}>Blended Average</p>
            <p className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>{blendedAverage}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Average of community + external review sources</p>
          </div>
          <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, rgba(57,255,20,0.20), rgba(57,255,20,0.05))', border: '1px solid rgba(57,255,20,0.5)' }}>
            <p className="text-xs uppercase font-mono mb-1" style={{ color: '#39FF14' }}>Community Reviews</p>
            <p className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>{Number(summary?.total_reviews || 0)}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Approved user-submitted reviews</p>
          </div>
          <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.20), rgba(0,212,255,0.05))', border: '1px solid rgba(0,212,255,0.5)' }}>
            <p className="text-xs uppercase font-mono mb-1" style={{ color: '#00D4FF' }}>External Sources</p>
            <p className="text-3xl font-black" style={{ color: 'var(--text-primary)' }}>{sourceAverages.length}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>G2/Capterra/Trustpilot/Google when available</p>
          </div>
        </div>

        <div className="p-4 mb-6" style={{ borderLeft: '4px solid #FFE800', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
          <p className="font-mono text-sm mb-2" style={{ color: 'var(--accent-primary)' }}>AI Summary</p>
          <div className="text-sm space-y-2" style={{ color: 'var(--text-secondary)' }}>
            <p><strong style={{ color: 'var(--text-primary)' }}>Pros:</strong> {(summary?.ai_summary?.pros || []).join(' | ') || 'No summary yet'}</p>
            <p><strong style={{ color: 'var(--text-primary)' }}>Cons:</strong> {(summary?.ai_summary?.cons || []).join(' | ') || 'No summary yet'}</p>
            <p><strong style={{ color: 'var(--text-primary)' }}>Ideal user:</strong> {summary?.ai_summary?.ideal_user || 'General teams'}</p>
          </div>
        </div>

        {reviewSignals.length > 0 && (
          <div className="p-4 mb-6" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Website Review Signals</p>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {reviewSignals.slice(0, 6).map((line, index) => (
                <li key={`${line}-${index}`} className="flex items-start gap-2">
                  <span style={{ color: 'var(--accent-primary)' }}>-</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {reviewSources.length > 0 && (
          <div className="p-4 mb-6" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>External Review Sources</p>
            <div className="space-y-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {reviewSources.slice(0, 3).map((source, index) => (
                <div key={`${source.url || source.host || index}-${index}`}>
                  <p className="font-mono text-xs mb-1" style={{ color: 'var(--accent-primary)' }}>
                    {source.host || 'review source'}
                    {source.url ? (
                      <a href={source.url} target="_blank" rel="noopener noreferrer" className="ml-2" style={{ color: 'var(--text-secondary)' }}>
                        open
                      </a>
                    ) : null}
                  </p>
                  <ul className="space-y-1">
                    {(source.snippets || []).slice(0, 2).map((snippet, snippetIndex) => (
                      <li key={`${snippet}-${snippetIndex}`} className="flex items-start gap-2">
                        <span style={{ color: 'var(--accent-primary)' }}>-</span>
                        <span>{snippet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {sourceAverages.length > 0 && (
          <div className="p-4 mb-6" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Review Site Averages</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sourceAverages.map((site, index) => (
                <div key={`${site.host || index}-${index}`} className="p-3 rounded" style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.03)' }}>
                  <p className="text-xs font-mono mb-1" style={{ color: '#00D4FF' }}>{site.host || 'source'}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{Number(site.average_rating || 0).toFixed(2)} / 5</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{Number(site.review_count || 0)} reviews detected</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Star Breakdown</p>
            {['5', '4', '3', '2', '1'].map((star) => {
              const count = Number(summary?.star_breakdown?.[star] || 0);
              const total = Number(summary?.total_reviews || 0);
              const width = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={star} className="mb-2">
                  <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                    <span>{star}⚙️ = {count}</span>
                    <span>{width}%</span>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.1)', height: '6px' }}>
                    <div style={{ width: `${width}%`, background: '#FFE800', height: '100%' }}></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>FTC Disclosure</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Reviews are independent and unfiltered. Sponsored tool partnerships do not influence reviewer ratings or comments.
            </p>
          </div>
        </div>

        <form onSubmit={submitReview} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="label-pro">Your name</label>
            <input className="input-pro" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} required />
          </div>
          <div>
            <label className="label-pro">Star rating</label>
            <select className="input-pro" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })}>
              {[5, 4, 3, 2, 1].map((score) => (
                <option key={score} value={score}>{score} stars</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-pro">Role</label>
            <select className="input-pro" value={form.role_profile} onChange={(e) => setForm({ ...form, role_profile: e.target.value })}>
              <option value="">Select role profile</option>
              {['Founder', 'Marketer', 'Operations', 'Developer', 'Designer', 'Customer Success', 'Sales', 'Other'].map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-pro">Company size</label>
            <select className="input-pro" value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })}>
              <option value="">Select company size</option>
              {['Solo', '2-10', '11-50', '51-200', '201-1000', '1000+'].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-pro">Review title</label>
            <input className="input-pro" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="label-pro">Review (target 300 words)</label>
            <textarea
              className="input-pro"
              rows="7"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Share practical outcomes, limitations, and who this tool is best for."
              required
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Word count: {wordCount} (minimum 50)</p>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit review'}</button>
            {submitMessage && <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{submitMessage}</span>}
          </div>
        </form>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select className="input-pro max-w-[220px]" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">All roles</option>
            {roles.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
          <select className="input-pro max-w-[220px]" value={companySizeFilter} onChange={(e) => setCompanySizeFilter(e.target.value)}>
            <option value="all">All company sizes</option>
            {companySizes.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Showing {filteredReviews.length} of {reviews.length} reviews</p>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-secondary)' }}>Loading reviews...</p>
        ) : filteredReviews.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No reviews yet. Be the first to review this tool.</p>
        ) : (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <div key={review.id} className="p-4" style={{ background: 'var(--bg-surface)', border: '1px solid rgba(255,255,255,0.10)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {review.author_name || 'Anonymous'}
                    {review.verified && (
                      <span className="ml-2 text-xs" style={{ color: '#39FF14' }}>Wowrolla Verified</span>
                    )}
                  </div>
                  <div className="text-sm" style={{ color: '#FFE800' }}>{review.rating}/5</div>
                </div>
                {review.title && <h4 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{review.title}</h4>}
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{review.body}</p>
                <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {review.role_profile ? `${review.role_profile}` : 'Role n/a'}
                  {review.company_size ? ` - ${review.company_size}` : ''}
                  {review.source_label ? ` - Source: ${review.source_label}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const ToolPricingTab = ({ tool }) => {
  const pricingTab = tool?.showcase_tabs?.pricing || {};
  const tiers = tool.pricing_tiers?.length ? tool.pricing_tiers : (pricingTab.tiers || []);
  const pricingLines = pricingTab.pricing_lines || [];

  return (
    <section className="container mx-auto px-6 py-10" data-testid="tool-pricing-tab">
      <div className="terminal-grid p-8 mb-6">
        <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Pricing Plans</h3>
        {tiers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers.map((tier, index) => (
              <div key={`${tier.name}-${index}`} className="p-5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{tier.name}</h4>
                <div className="text-2xl font-black mb-3" style={{ color: 'var(--accent-primary)' }}>{tier.price}</div>
                <ul className="space-y-2">
                  {(tier.features || []).map((feature, idx) => (
                    <li key={idx} className="text-sm" style={{ color: 'var(--text-secondary)' }}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>Pricing tiers are not available yet for this tool.</p>
        )}

        {(pricingLines.length > 0 || pricingTab.pricing_url) && (
          <div className="mt-6 p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Source Trace</p>
            {pricingTab.pricing_url && (
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                Pricing source:{' '}
                <a href={pricingTab.pricing_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>
                  {pricingTab.pricing_url}
                </a>
              </p>
            )}
            {pricingLines.length > 0 && (
              <ul className="space-y-1 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                {pricingLines.slice(0, 8).map((line, idx) => (
                  <li key={`${line}-${idx}`}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="terminal-grid p-8">
        <h4 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Pricing Monitor</h4>
        <p style={{ color: 'var(--text-secondary)' }}>Historical pricing timeline will appear here after snapshots are enabled.</p>
      </div>
    </section>
  );
};

const ToolChangelogTab = ({ tool }) => {
  const changelogTab = tool?.showcase_tabs?.changelog || {};
  const tabEntries = (changelogTab.entries || []).map((entry) => ({
    title: 'Website update signal',
    content: entry,
  }));
  const changelogSources = (changelogTab.sources || []).filter(Boolean);
  const changelogBlocks = (tool.content_blocks || []).filter((block) => block.type === 'changelog');
  const changelogSnapshotBlocks = (tool.content_blocks || []).filter((block) => block.type === 'changelog_snapshot');
  const fallbackEntries = [
    ...changelogSnapshotBlocks.flatMap((block) => (block.entries || []).map((entry) => ({ title: block.title || 'Product update', content: entry }))),
    ...tabEntries,
  ];
  const allEntries = changelogBlocks.length > 0
    ? changelogBlocks.map((block) => ({ title: block.title || 'Update', content: block.content || block.description || 'No details provided.' }))
    : fallbackEntries;
  const latestEntries = allEntries.slice(0, 5);

  return (
    <section className="container mx-auto px-6 py-10" data-testid="tool-changelog-tab">
      <div className="terminal-grid p-8">
        <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>Changelog</h3>
        {latestEntries.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No changelog entries yet. Updates will be listed here.</p>
        ) : (
          <div className="space-y-4">
            {latestEntries.map((entry, index) => (
              <div key={index} className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{entry.title || `Update ${index + 1}`}</h4>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{entry.content || entry.description || 'No details provided.'}</p>
              </div>
            ))}
          </div>
        )}

        {changelogSources.length > 0 && (
          <div className="mt-5 p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Changelog Sources</p>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {changelogSources.slice(0, 3).map((source, index) => (
                <li key={`${source.url || source.host || index}-${index}`}>
                  <span style={{ color: 'var(--accent-primary)' }}>{source.host || 'source'}</span>
                  {source.url ? (
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="ml-2" style={{ color: 'var(--text-secondary)' }}>
                      open
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

const ToolRoiTab = ({ tool }) => {
  const roiConfig = tool?.showcase_tabs?.roi || {};
  const [monthlyCost, setMonthlyCost] = useState(() => getDefaultMonthlyCost(tool));
  const [hoursSaved, setHoursSaved] = useState(() => Number(roiConfig.hours_saved_per_month) || 10);
  const [hourlyValue, setHourlyValue] = useState(() => Number(roiConfig.value_per_hour) || 50);
  const [selectedPlan, setSelectedPlan] = useState('custom');

  const pricedPlans = (tool?.pricing_tiers || [])
    .map((tier) => ({ ...tier, monthlyCost: parseMonthlyCost(tier?.price) }))
    .filter((tier) => tier.monthlyCost !== null && tier.monthlyCost >= 0);

  useEffect(() => {
    const nextRoiConfig = tool?.showcase_tabs?.roi || {};
    setMonthlyCost(getDefaultMonthlyCost(tool));
    setHoursSaved(Number(nextRoiConfig.hours_saved_per_month) || 10);
    setHourlyValue(Number(nextRoiConfig.value_per_hour) || 50);
    setSelectedPlan('custom');
  }, [tool]);

  const monthlyGain = hoursSaved * hourlyValue;
  const roiPercent = monthlyCost > 0 ? ((monthlyGain - monthlyCost) / monthlyCost) * 100 : 0;

  return (
    <section className="container mx-auto px-6 py-10" data-testid="tool-roi-tab">
      <div className="terminal-grid p-8">
        <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>ROI Calculator</h3>
        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>Estimate business impact for adopting {tool.name}.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Plan Selection
            <select
              value={selectedPlan}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedPlan(value);
                if (value === 'custom') return;
                const match = pricedPlans.find((plan, index) => `${index}` === value);
                if (match && match.monthlyCost !== null) {
                  setMonthlyCost(Number(match.monthlyCost));
                }
              }}
              className="w-full mt-2 px-4 py-3 bg-transparent border border-white/20"
              style={{ color: 'var(--text-primary)' }}
            >
              <option value="custom">Custom price input</option>
              {pricedPlans.map((plan, index) => (
                <option key={`${plan.name}-${index}`} value={`${index}`}>
                  {plan.name}: {plan.price}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Monthly Tool Cost
            <input
              type="number"
              min="0"
              value={monthlyCost}
              onChange={(e) => setMonthlyCost(Number(e.target.value) || 0)}
              className="w-full mt-2 px-4 py-3 bg-transparent border border-white/20"
              style={{ color: 'var(--text-primary)' }}
            />
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Hours Saved / Month
            <input
              type="number"
              min="0"
              value={hoursSaved}
              onChange={(e) => setHoursSaved(Number(e.target.value) || 0)}
              className="w-full mt-2 px-4 py-3 bg-transparent border border-white/20"
              style={{ color: 'var(--text-primary)' }}
            />
          </label>
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Value per Hour
            <input
              type="number"
              min="0"
              value={hourlyValue}
              onChange={(e) => setHourlyValue(Number(e.target.value) || 0)}
              className="w-full mt-2 px-4 py-3 bg-transparent border border-white/20"
              style={{ color: 'var(--text-primary)' }}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <div className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>Monthly Gain</div>
            <div className="text-2xl font-black" style={{ color: 'var(--accent-secondary)' }}>${monthlyGain.toFixed(2)}</div>
          </div>
          <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <div className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>Monthly Net</div>
            <div className="text-2xl font-black" style={{ color: 'var(--accent-primary)' }}>${(monthlyGain - monthlyCost).toFixed(2)}</div>
          </div>
          <div className="p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
            <div className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>ROI %</div>
            <div className="text-2xl font-black" style={{ color: roiPercent >= 0 ? 'var(--accent-secondary)' : '#ff5b5b' }}>{roiPercent.toFixed(1)}%</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToolDetailPage;

